import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { addMetadataToExperience } from '../../analytics';
import { ACTION_ERROR, ACTION_ERROR_FALLBACK, ACTION_RELOADING, ACTION_RESOLVED, ACTION_UPDATE_METADATA_STATUS, APIError, cardAction, getStatus } from '@atlaskit/linking-common';
import { ERROR_MESSAGE_FATAL, ERROR_MESSAGE_METADATA, ERROR_MESSAGE_OAUTH } from '../../actions/constants';
import { getUnauthorizedJsonLd } from '../../../utils/jsonld';
import { SmartLinkStatus } from '../../../constants';
import { useSmartLinkContext } from '@atlaskit/link-provider';
var useResolve = function useResolve() {
  // Request JSON-LD data for the card from ORS, if it has extended
  // its cache lifespan OR there is no data for it currently. Once the data
  // has come back asynchronously, dispatch the resolved action for the card.
  var _useSmartLinkContext = useSmartLinkContext(),
    store = _useSmartLinkContext.store,
    connections = _useSmartLinkContext.connections,
    config = _useSmartLinkContext.config;
  var getState = store.getState,
    dispatch = store.dispatch;
  var hasAuthFlowSupported = config.authFlow !== 'disabled';
  var setMetadataStatus = useCallback(function (url, metadataStatus) {
    dispatch(cardAction(ACTION_UPDATE_METADATA_STATUS, {
      url: url
    }, undefined, undefined, metadataStatus));
  }, [dispatch]);
  var handleResolvedLinkError = useCallback(function (url, error, response, isMetadataRequest) {
    var _ref = getState()[url] || {
        status: SmartLinkStatus.Pending,
        details: undefined
      },
      details = _ref.details;
    var hasData = !!(details && details.data);

    // If metadata request then set metadata status, return and do not alter link status
    if (isMetadataRequest) {
      setMetadataStatus(url, 'errored');
      return;
    }
    if (error.kind === 'fatal') {
      // If there's no previous data in the store for this URL, then bail
      // out and let the editor handle fallbacks (returns to a blue link).
      if (!hasData && status !== 'resolved') {
        dispatch(cardAction(ACTION_ERROR, {
          url: url
        }, details, error));
        throw error;
      }
      // If we already have resolved data for this URL in the store, then
      // simply fallback to the previous data.
      if (hasData) {
        dispatch(cardAction(ACTION_RESOLVED, {
          url: url
        }, details));
      }
      // Handle AuthErrors (user did not have access to resource) -
      // Missing AAID in ASAP claims, or missing UserContext, or 403 from downstream
    } else if (error.kind === 'auth') {
      dispatch(cardAction(ACTION_RESOLVED, {
        url: url
      }, getUnauthorizedJsonLd()));
    } else {
      if (error.kind === 'fallback') {
        // Fallback to blue link with smart link formatting. Not part of reliability.
        dispatch(cardAction(ACTION_ERROR_FALLBACK, {
          url: url
        }, response, error));
      } else {
        // Fallback to blue link with smart link formatting. Part of reliability.
        dispatch(cardAction(ACTION_ERROR, {
          url: url
        }, undefined, error));
      }
    }
  }, [getState, setMetadataStatus, dispatch]);
  var handleResolvedLinkSuccess = useCallback(function (resourceUrl, response, isReloading, isMetadataRequest) {
    //if a link resolves normally, metadata will also always be resolved
    setMetadataStatus(resourceUrl, 'resolved');
    // Dispatch Analytics and resolved card action - including unauthorized states.
    if (isReloading) {
      dispatch(cardAction(ACTION_RELOADING, {
        url: resourceUrl
      }, response));
    } else {
      dispatch(cardAction(ACTION_RESOLVED, {
        url: resourceUrl
      }, response, undefined, undefined, isMetadataRequest));
    }
  }, [setMetadataStatus, dispatch]);
  var handleResolvedLinkResponse = useCallback(function (resourceUrl, response) {
    var isReloading = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var isMetadataRequest = arguments.length > 3 ? arguments[3] : undefined;
    var hostname = new URL(resourceUrl).hostname;
    var nextStatus = response ? getStatus(response) : 'fatal';

    // If we require authorization & do not have an authFlow available,
    // throw an error and render as a normal blue link.
    if ((nextStatus === 'unauthorized' || nextStatus === 'forbidden') && !hasAuthFlowSupported) {
      handleResolvedLinkError(resourceUrl, new APIError('fallback', hostname, ERROR_MESSAGE_OAUTH), response, isMetadataRequest);
      return;
    }

    // Handle any other errors
    if (nextStatus === 'fatal') {
      handleResolvedLinkError(resourceUrl, new APIError('fatal', hostname, ERROR_MESSAGE_FATAL), undefined, isMetadataRequest);
      return;
    }

    // Handle errors of any other kind in the metadata request response
    if (isMetadataRequest && nextStatus !== 'resolved') {
      handleResolvedLinkError(resourceUrl, new APIError('error', hostname, ERROR_MESSAGE_METADATA), response, isMetadataRequest);
      return;
    }

    /**
     * Using unstable_batchedUpdates because this store update happens async and trigers a rerender
     * more info:
     * https://github.com/facebook/react/blob/v18.2.0/packages/use-sync-external-store/src/useSyncExternalStoreShimClient.js#L113
     * https://react-redux.js.org/api/batch
     */
    unstable_batchedUpdates(function () {
      handleResolvedLinkSuccess(resourceUrl, response, isReloading, isMetadataRequest);
    });
  }, [handleResolvedLinkError, handleResolvedLinkSuccess, hasAuthFlowSupported]);
  return useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(url) {
      var isReloading,
        isMetadataRequest,
        id,
        _ref3,
        details,
        hasData,
        _args = arguments;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            isReloading = _args.length > 1 && _args[1] !== undefined ? _args[1] : false;
            isMetadataRequest = _args.length > 2 && _args[2] !== undefined ? _args[2] : false;
            id = _args.length > 3 && _args[3] !== undefined ? _args[3] : '';
            _ref3 = getState()[url] || {
              status: SmartLinkStatus.Pending,
              details: undefined
            }, details = _ref3.details;
            hasData = !!(details && details.data);
            if (!(isReloading || !hasData || isMetadataRequest)) {
              _context.next = 9;
              break;
            }
            return _context.abrupt("return", connections.client.fetchData(url, isReloading).then(function (response) {
              return handleResolvedLinkResponse(url, response, isReloading, isMetadataRequest);
            }).catch(function (error) {
              return handleResolvedLinkError(url, error, undefined, isMetadataRequest);
            }));
          case 9:
            addMetadataToExperience('smart-link-rendered', id, {
              cached: true
            });
          case 10:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [connections.client, getState, handleResolvedLinkError, handleResolvedLinkResponse]);
};
export default useResolve;